import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAddPlanzoneResourceToProjects,
  useRetrieveResourceProjects,
} from "@planzone/view-models";
import { RoleSelection } from "./roleSelection";
import { Safeguard } from "@scenes/safeguard";
import { Modal } from "@common/modal";
import { Props } from "./types";
import styles from "./styles.scss";
import { UseCases } from "@planzone/core";
import { ProjectsSelection } from "./projectsSelection";
import { usePreferences } from "@components/preferences";
import { Button } from "@common/button";
import {
  filter as f,
  Identifiable,
  retrieveResourceProjectsQuery,
  Role,
} from "@planzone/dto";

function AddPlanzoneResourceToProjects({ cancel, resource }: Props) {
  const { t } = useTranslation();
  const { state, edit } = usePreferences();
  const query = { ...retrieveResourceProjectsQuery, limit: 20 };

  const [filter, setFilter] = React.useState(f);
  const [isRoleOpen, setIsRoleOpen] = useState(false);
  const [selectedRole, setSelectedRole] = React.useState<Identifiable<Role>>();
  const selectedProjects = filter?.projects || [];
  const {
    isRequestPending: isPending,
    isRequestSuccess: isSuccess,
    isRequestFailure: isFailure,
    projects,
    retrieveResourceProjects,
  } = useRetrieveResourceProjects(resource, query);

  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    addPlanzoneResourceToProjects,
  } = useAddPlanzoneResourceToProjects(resource);

  React.useEffect(() => {
    if (projects) {
      const resourceProjectUuids = projects.map((project) => project);
      const updatedFilter = {
        ...filter,
        projects: projects,
      };
      setFilter(updatedFilter);
    }
  }, [projects, setFilter]);

  return (
    <Modal header={t("addPlanzoneResourceToProjects.title")} close={cancel}>
      <Safeguard useCases={[UseCases.addPlanzoneResourceToProjects]}>
        {({ canAddPlanzoneResourceToProjects }) =>
          canAddPlanzoneResourceToProjects && (
            <div className={styles.addPlanzoneResourceToProjects}>
              <div className={styles.filterContainer}>
                <div>
                  <ProjectsSelection
                    filter={filter}
                    resource={resource}
                    placeholder={t(
                      "addPlanzoneResourceToProjects.inputProject",
                    )}
                    setFilter={setFilter}
                  />
                </div>
              </div>
              <div className={styles.col}>
                <div className={styles.roleInput}>
                  <h2>{t("addPlanzoneResourceToProjects.inputRole")}</h2>
                  <RoleSelection
                    placeholder={t("addPlanzoneResourceToProjects.inputRole")}
                    selectedRole={selectedRole}
                    select={setSelectedRole}
                    isRoleSelectionOpen={isRoleOpen}
                    setIsRoleSelectionOpen={setIsRoleOpen}
                  />
                </div>
              </div>
              <div className={styles.footer}>
                <Button type="cancel" press={cancel}>
                  {t("addPlanzoneResourceToProjects.cancel")}
                </Button>
                <Button
                  type="submit"
                  status={isRequestPending ? "pending" : "none"}
                  isDisabled={isRequestPending}
                  press={() =>
                    addPlanzoneResourceToProjects({
                      resource,
                      projects: selectedProjects.filter(
                        (project) =>
                          !projects.find(
                            (resProject) => resProject.uuid === project.uuid,
                          ),
                      ),
                      role: selectedRole ? selectedRole : ([] as any),
                    })
                  }
                >
                  {t("addPlanzoneResourceToProjects.submit")}
                </Button>
              </div>
            </div>
          )
        }
      </Safeguard>
    </Modal>
  );
}

export { AddPlanzoneResourceToProjects };
